import {
  Button,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useStyleConfig,
  VStack,
} from "@chakra-ui/react";
import {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
// import User from "../../ui_components/reusables";
import { Input, useCustomToast } from "../../ui_components/reusables";
import { Form, Formik } from "formik";
import MusicCard from "../../ui_components/music/musicCard";
import dayjs from "../../helpers/dayJs";
import { FiClock } from "react-icons/fi";
import { useFirebase } from "../firebase";
import useUserStore from "../../store/userStore";
import { shallow } from "zustand/shallow";
import isEmpty from "validator/lib/isEmpty";
import { serverTimestamp } from "firebase/firestore";
import User from "../../ui_components/user";

/**
 * @typedef {Object} ReportManagerContextValue
 * @property {(userObject) => void} triggerReportProfile
 * @property {(postObject) => void} triggerClaimContribution
 * @property {(userObject) => void} triggerBusinessQuery
 * @property {(userObject) => void} triggerFanMail
 * @property {() => void} triggerReportProblem
 * @property {() => void} triggerReportContent
 */

const ReportManagerContext = createContext(null);

const ContentTypes = {
  REPORT_PROFILE: { header: "Report a profile" },
  CLAIM_CONTRIBUTION: { header: "Claim contribution" },
  REPORT_CONTENT: { header: "Report a content" },
  SEND_FANMAIL: { header: "Send fan-mail" },
  SEND_BUSINESSQUERY: { header: "Send business inquiry" },
  REPORT_PROBLEM: { header: "Get help" },
};

function ReportManager({ children }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contentType, setContentType] = useState();
  const [modalData, setModalData] = useState(null);
  const { addDocument } = useFirebase();
  const user = useUserStore((state) => state.user, shallow) || {};

  const {
    uid: stateUserId,
    username: stateUsername,
    fullName: stateFullname,
  } = user;
  function triggerModal() {
    setIsModalOpen((prev) => !prev);
  }
  const toast = useCustomToast();

  function triggerReportProfile(data) {
    setModalData(data);
    setContentType(ContentTypes.REPORT_PROFILE);
    triggerModal();
  }

  function triggerClaimContribution(data) {
    setModalData(data);
    setContentType(ContentTypes.CLAIM_CONTRIBUTION);
    triggerModal();
  }

  function triggerReportProblem() {
    setModalData(null);
    setContentType(ContentTypes.REPORT_PROBLEM);
    triggerModal();
  }

  function triggerReportContent() {
    setModalData(null);
    setContentType(ContentTypes.REPORT_CONTENT);
    triggerModal();
  }

  function triggerFanMail(data) {
    setModalData(data);
    setContentType(ContentTypes.SEND_FANMAIL);
    triggerModal();
  }

  function triggerBusinessQuery(data) {
    setModalData(data);
    setContentType(ContentTypes.SEND_BUSINESSQUERY);
    triggerModal();
  }

  function validateForm(values) {
    const errors = {};
    Object.keys(values).map((key) => {
      if (isEmpty(values[key]) || !values[key]) {
        errors[key] = "This field is required";
      }
    });

    return errors;
  }

  async function submitUniversalReport({ reportBody }) {
    await addDocument({
      path: `reports`,
      data: {
        reporterUserId: stateUserId,
        details: { reportBody, fromUrl: window.location.href },
        createdAt: serverTimestamp(),
      },
    });
    setModalData(null);
    triggerModal();
    toast({
      status: "success",
      title: "Report submitted",
      description: "We will review your report and take actions accordingly.",
    });
  }

  const ReportProfile = memo(({ user }) => {
    async function submitReport({ reportBody }) {
      await addDocument({
        path: `reports`,
        data: {
          accusedUserId: user.id,
          reporterUserId: stateUserId,
          details: { reportBody, fromUrl: window.location.href },
          createdAt: serverTimestamp(),
        },
      });
      setModalData(null);
      triggerModal();
      toast({
        status: "success",
        title: "Report submitted",
        description: "We will review your report and take actions accordingly.",
      });
    }

    return (
      user && (
        <VStack w="full" h="max-content" color="white" align={"flex-start"}>
          <Text
            bg="#ECC94B30"
            color="orange.200"
            py="5px"
            px="10px"
            borderRadius={"md"}
            fontSize={"12px"}
          >
            <b>Note:</b> Submitting a false report may result in your account
            being suspended or banned.
          </Text>
          <Text fontSize={"sm"}>You are reporting the profile:</Text>
          <User.Regular {...user} />
          <Formik
            initialValues={{ reportBody: "" }}
            onSubmit={submitReport}
            validate={validateForm}
            validateOnChange={false}
          >
            {({ isSubmitting }) => (
              <Form style={{ width: "100%" }}>
                <VStack w="full" align={"flex-start"}>
                  <Input
                    as="textarea"
                    h="130px"
                    w="full"
                    placeholder="Tell us why you are reporting the profile..."
                    py="2"
                    mt="2"
                    name="reportBody"
                  />
                  <Button
                    size="sm"
                    w="full"
                    type="submit"
                    isLoading={isSubmitting}
                  >
                    Submit
                  </Button>
                </VStack>
              </Form>
            )}
          </Formik>
        </VStack>
      )
    );
  });

  const ClaimContribution = memo(({ post }) => {
    const styles = useStyleConfig("Button", { variant: "nav" });
    async function submitReport({ reportBody }) {
      await addDocument({
        path: `reports`,
        data: {
          reporterUserId: stateUserId,
          details: { reportBody, fromUrl: window.location.href },
          createdAt: serverTimestamp(),
        },
      });
      await addDocument({
        path: `profiles/${post.user.id}/posts/${post.id}/claims`,
        data: {
          reporterUserId: stateUserId,
          details: reportBody,
          createdAt: serverTimestamp(),
        },
      });
      setModalData(null);
      triggerModal();
      toast({
        status: "success",
        title: "Report submitted",
        description: "The author has been notified of your claim.",
      });
    }

    function validateForm(values) {}

    return (
      <VStack w="full" h="max-content" color="white" align={"flex-start"}>
        <Text
          bg="#ECC94B30"
          color="orange.200"
          py="5px"
          px="10px"
          borderRadius={"md"}
          fontSize={"12px"}
          fontWeight={"semibold"}
        >
          Upon submission, the author or uploader will be notified about your
          claim. They can either approve or deny your claim. You can further
          appeal for your claim via <u>contacting support</u>.
        </Text>
        <Text fontSize={"sm"}>You are claiming contribution for:</Text>
        <VStack
          __css={styles}
          w="full"
          h="max-content"
          borderRadius={"md"}
          align={"flex-start"}
          p="2"
          px="2.5"
          spacing={"1"}
        >
          <Text fontSize={"sm"} fontWeight={"semibold"}>
            {post?.title}
          </Text>
          <Text fontSize={"sm"} color="gray.400">
            by {post?.user?.fullName}
          </Text>
          <Text
            fontSize={"sm"}
            pt="2"
            color="gray.400"
            display={"flex"}
            alignItems={"center"}
            gap="2"
          >
            <FiClock />
            {dayjs.utc(post?.duration * 1000).format("m:ss")}
          </Text>
        </VStack>
        <Formik
          initialValues={{ reportBody: "" }}
          onSubmit={submitReport}
          validate={validateForm}
          validateOnChange={false}
        >
          {({ isSubmitting }) => (
            <Form style={{ width: "100%" }}>
              <VStack w="full" align={"flex-start"}>
                <Input
                  as="textarea"
                  h="130px"
                  w="full"
                  placeholder="Describe your specific contribution to this content..."
                  py="2"
                  mt="2"
                  name="reportBody"
                />
                <Button
                  size="sm"
                  w="full"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Submit
                </Button>
              </VStack>
            </Form>
          )}
        </Formik>
      </VStack>
    );
  });

  const ReportProblem = memo(({}) => {
    return (
      <VStack w="full" h="max-content" color="white" align={"flex-start"}>
        <Text
          bg="#ECC94B30"
          color="orange.200"
          py="5px"
          px="10px"
          borderRadius={"md"}
          fontSize={"12px"}
        >
          <b>Note:</b> After you submit a support ticket, we will notify you
          about the updates or contact you, all via email.
        </Text>
        <Formik
          initialValues={{ reportBody: "" }}
          onSubmit={submitUniversalReport}
          validate={validateForm}
          validateOnChange={false}
        >
          {({ isSubmitting }) => (
            <Form style={{ width: "100%" }}>
              <VStack w="full" align={"flex-start"}>
                <Input
                  as="textarea"
                  h="130px"
                  w="full"
                  placeholder="Tell us how we can help you..."
                  py="2"
                  mt="2"
                  name="reportBody"
                />
                <Button
                  size="sm"
                  w="full"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Submit
                </Button>
              </VStack>
            </Form>
          )}
        </Formik>
      </VStack>
    );
  });

  const ReportContent = memo(({}) => {
    return (
      <VStack w="full" h="max-content" color="white" align={"flex-start"}>
        <Text
          bg="#ECC94B30"
          color="orange.200"
          py="5px"
          px="10px"
          borderRadius={"md"}
          fontSize={"12px"}
        >
          <b>Note:</b> Submitting a false report may result in your account
          being suspended or banned.
        </Text>
        <Formik
          initialValues={{ reportBody: "" }}
          onSubmit={submitUniversalReport}
          validate={validateForm}
          validateOnChange={false}
        >
          {({ isSubmitting }) => (
            <Form style={{ width: "100%" }}>
              <VStack w="full" align={"flex-start"}>
                <Input
                  as="textarea"
                  h="130px"
                  w="full"
                  placeholder="Tell us why you are reporting this content..."
                  py="2"
                  mt="2"
                  name="reportBody"
                />
                <Button
                  size="sm"
                  w="full"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Submit
                </Button>
              </VStack>
            </Form>
          )}
        </Formik>
      </VStack>
    );
  });

  const FanMail = memo(({ user }) => {
    async function submitReport({ reportBody }) {
      await addDocument({
        path: `profiles/${user.id}/messages`,
        data: {
          fanUserId: stateUserId,
          fanUserUsername: stateUsername,
          fanUserFullname: stateFullname,
          message: reportBody,
          createdAt: serverTimestamp(),
          type: "fanmail",
          isRead: false,
          status: "unknown",
        },
      });
      toast({
        status: "success",
        title: "Message sent",
        description: `Sent fanmail to ${user.fullName}`,
      });
      setModalData(null);
      triggerModal();
    }

    function validateForm(values) {}

    return (
      user && (
        <VStack w="full" h="max-content" color="white" align={"flex-start"}>
          <User.Regular {...user} />
          <Formik
            initialValues={{ reportBody: "" }}
            onSubmit={submitReport}
            validate={validateForm}
            validateOnChange={false}
          >
            {({ isSubmitting }) => (
              <Form style={{ width: "100%" }}>
                <VStack w="full" align={"flex-start"}>
                  <Input
                    as="textarea"
                    h="130px"
                    w="full"
                    placeholder="Write something nice as a fan..."
                    py="2"
                    mt="2"
                    name="reportBody"
                  />
                  <Button
                    size="sm"
                    w="full"
                    type="submit"
                    isLoading={isSubmitting}
                  >
                    Send Mail
                  </Button>
                </VStack>
              </Form>
            )}
          </Formik>
        </VStack>
      )
    );
  });

  const BusinessInquiry = memo(({ user }) => {
    async function submitReport({ reportBody }) {
      await addDocument({
        path: `profiles/${user.id}/messages`,
        data: {
          fanUserId: stateUserId,
          message: reportBody,
          createdAt: serverTimestamp(),
          type: "business",
          isRead: false,
          status: "unknown",
        },
      });
      toast({
        status: "success",
        title: "Message sent",
        description: `${user.fullName} has been notified about your inquiry`,
      });
      setModalData(null);
      triggerModal();
    }

    function validateForm(values) {}

    return (
      user && (
        <VStack w="full" h="max-content" color="white" align={"flex-start"}>
          <Text
            bg="#ECC94B30"
            color="orange.200"
            py="5px"
            px="10px"
            borderRadius={"md"}
            fontSize={"12px"}
          >
            <b>Note:</b> If your message is marked as spam, it might eventually
            result in your account being suspended or banned.
          </Text>
          <Text fontSize={"sm"}>You are reaching out to:</Text>
          <User.Regular {...user} />
          <Formik
            initialValues={{ reportBody: "" }}
            onSubmit={submitReport}
            validate={validateForm}
            validateOnChange={false}
          >
            {({ isSubmitting }) => (
              <Form style={{ width: "100%" }}>
                <VStack w="full" align={"flex-start"}>
                  <Input
                    as="textarea"
                    h="130px"
                    w="full"
                    placeholder="Write your message here..."
                    py="2"
                    mt="2"
                    name="reportBody"
                  />
                  <Button
                    size="sm"
                    w="full"
                    type="submit"
                    isLoading={isSubmitting}
                  >
                    Send Query
                  </Button>
                </VStack>
              </Form>
            )}
          </Formik>
        </VStack>
      )
    );
  });

  const value = useMemo(() => ({
    triggerReportProfile,
    triggerReportProblem,
    triggerFanMail,
    triggerBusinessQuery,
    triggerReportContent,
    triggerClaimContribution,
  }));

  return (
    <ReportManagerContext.Provider value={value}>
      <Modal
        motionPreset="slideInTop"
        size="lg"
        isOpen={isModalOpen}
        onClose={triggerModal}
        scrollBehavior="outside"
      >
        <ModalOverlay />
        <ModalContent maxH="100vh" className="gradient-border" pb="4">
          <ModalHeader>
            {contentType?.header}
            <ModalCloseButton />
          </ModalHeader>
          {contentType === ContentTypes.REPORT_PROFILE && (
            <ReportProfile user={modalData} />
          )}
          {contentType === ContentTypes.CLAIM_CONTRIBUTION && (
            <ClaimContribution post={modalData} />
          )}
          {contentType === ContentTypes.REPORT_CONTENT && <ReportContent />}
          {contentType === ContentTypes.SEND_FANMAIL && (
            <FanMail user={modalData} />
          )}
          {contentType === ContentTypes.SEND_BUSINESSQUERY && (
            <BusinessInquiry user={modalData} />
          )}
          {contentType === ContentTypes.REPORT_PROBLEM && <ReportProblem />}
        </ModalContent>
      </Modal>
      {children}
    </ReportManagerContext.Provider>
  );
}

/**
 * Access HLS audio controls and state.
 * Must be used under a `HlsAudioWrapper`.
 *
 * @returns {ReportManagerContextValue}
 */
export function useReportManager() {
  const context = useContext(ReportManagerContext);
  if (!context) {
    throw new Error(
      "useReportManager must be used within a ReportManagerProvider"
    );
  }
  return context;
}

export default ReportManager;
